// ============================================================================
// useFinanceData.js - V17 ENTERPRISE (DATA CONTEXT ULTRA-ROBUST)
// PADRÃO AIRBNB/NUBANK/QUINTOANDAR:
//   1. Cache-First: DataContext/IndexedDB carrega IMEDIATAMENTE → UI <16ms
//   2. Background Sync: Firestore reconcilia depois (não bloqueia UI)
//   3. Firestore NUNCA sobrescreve com array vazio se local tem dados
//   4. Merge inteligente: Firestore complementa, não substitui
//   5. DataContext como fonte única de verdade (IndexedDB + Firestore)
//
// 🔥 V17: notifyLocalChange emite 'dataUpdated' → Reports atualiza en tiempo real
// 🔥 V16: getContext ASYNC — espera la instancia correctamente
// 🔥 V16: DataContext.addObserver en vez de subscribe (API real)
// 🔥 V16: lerDataContext async/await completo
// 🔥 V16: Inicialización simplificada: DataContext → localStorage → vacío
// 🔥 V16: Listener cross-tab mejorado con storage event
// 🔥 V16: Filtrado por companyId en localStorage
// 🔥 V16: Invalidación de caché post-operación
// ============================================================================
import { useState, useEffect, useCallback, useRef } from "react";
import DataContext from "../../lib/data/context/DataContext";

// Intervalo de polling quando Firestore não está disponível (ms)
const LOCAL_POLL_INTERVAL = 1500;

// Chave usada para disparar storage event entre abas
const FINANCE_STORAGE_EVENT_KEY = "imoveispro_finance_updated";

export const useFinanceData = (database, showToast, addNotification, getContext = null) => {
  const [registros,       setRegistrosRaw]   = useState([]);
  const [loading,         setLoading]        = useState(true);
  const [firestoreOnline, setFirestoreOnline] = useState(false);

  const [mes, setMes] = useState(() => {
    const hoje = new Date();
    return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, "0")}`;
  });

  // Refs de controle
  const unsubscribeRef   = useRef(null);   // Firestore unsubscribe
  const pollIntervalRef  = useRef(null);   // localStorage poll interval
  const lastKnownDataRef = useRef([]);     // última versão em RAM
  const mountedRef       = useRef(true);   // guard de desmontagem
  const firestoreAtivoRef = useRef(false); // V10: ref para evitar race conditions
  const dataContextRef    = useRef(null);  // 🔥 V16: Cache de la instancia DataContext
  const observerIdRef     = useRef(null);  // 🔥 V16: ID del observer

  // ── setRegistros blindado ─────────────────────────────────────────────────
  const setRegistros = useCallback((dataOrUpdater) => {
    if (typeof dataOrUpdater === "function") {
      setRegistrosRaw((prev) => {
        const next = dataOrUpdater(prev);
        const safe = Array.isArray(next) ? next : [];
        lastKnownDataRef.current = safe;
        return safe;
      });
    } else {
      const safe = Array.isArray(dataOrUpdater) ? dataOrUpdater : [];
      lastKnownDataRef.current = safe;
      setRegistrosRaw(safe);
    }
  }, []);

  // ── 🔥 V16: OBTENER DATACONTEXT (ASYNC, CON CACHE) ──────────────────────
  const getDataContext = useCallback(async () => {
    // Si ya tenemos instancia en cache, retornarla
    if (dataContextRef.current && dataContextRef.current.collections) {
      return dataContextRef.current;
    }

    // Si getContext externo es una función async
    if (typeof getContext === 'function') {
      try {
        const ctx = await getContext();
        if (ctx && ctx.collections) {
          dataContextRef.current = ctx;
          return ctx;
        }
      } catch (e) {
        console.warn('⚠️ [V17] getContext externo falló:', e.message);
      }
    }

    // Intentar obtener DataContext directamente del storage
    try {
      const authUser = JSON.parse(localStorage.getItem("auth_user") || "{}");
      const userId = authUser.uid || authUser.id || "1";
      
      if (userId && userId !== "1") {
        const ctx = await DataContext.getInstance(userId);
        if (ctx && ctx.collections) {
          dataContextRef.current = ctx;
          return ctx;
        }
      }
    } catch (e) {
      console.warn('⚠️ [V17] DataContext.getInstance falló:', e.message);
    }

    return null;
  }, [getContext]);

  // ── 🔥 V16: Leitura do DataContext (fonte primária, ASYNC) ───────────────
  const lerDataContext = useCallback(async () => {
    try {
      const ctx = await getDataContext();
      if (ctx?.collections?.financeiro?.read) {
        const dados = await ctx.collections.financeiro.read();
        if (Array.isArray(dados) && dados.length > 0) {
          console.log(`📦 [V17] DataContext: ${dados.length} registros financeiros`);
          return dados;
        }
      }
    } catch (e) {
      console.warn('⚠️ [V17] DataContext read falhou, usando fallback:', e.message);
    }
    return null;
  }, [getDataContext]);

  // ── 🔥 V16: Leitura do localStorage (fallback, filtrado por userId) ──────
  const lerLocalStorage = useCallback(() => {
    try {
      const authUser = JSON.parse(localStorage.getItem("auth_user") || "{}");
      const userId   = authUser.uid || authUser.id || "1";
      const companyId = authUser.companyId || authUser.company || null;
      const dbKey    = `imoveispro_db_user_${userId}`;
      const db       = JSON.parse(localStorage.getItem(dbKey) || "{}");
      
      // Filtrar por userId y opcionalmente por companyId
      const dados = (db.financeiro || []).filter((f) => {
        if (!f) return false;
        const matchUser = f.userId === userId || f.userId === String(userId);
        if (companyId) {
          const matchCompany = f.companyId === companyId || f.companyId === String(companyId);
          return matchUser && matchCompany;
        }
        return matchUser;
      });
      
      return Array.isArray(dados) ? dados : [];
    } catch {
      return [];
    }
  }, []);

  // ── Deduplicar por ID ─────────────────────────────────────────────────────
  const deduplicar = useCallback((arr) => {
    if (!Array.isArray(arr)) return [];
    const seen = new Map();
    for (const item of arr) {
      if (item && item.id) seen.set(item.id, item);
    }
    return Array.from(seen.values());
  }, []);

  // ── Iniciar polling reativo do localStorage ───────────────────────────────
  const iniciarPollingLocal = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    const poll = () => {
      if (!mountedRef.current) return;
      const dados = deduplicar(lerLocalStorage());

      // Só atualiza se houve mudança real (evita re-renders desnecessários)
      const prevIds = lastKnownDataRef.current.map((r) => r.id).join(",");
      const nextIds = dados.map((r) => r.id).join(",");
      if (prevIds !== nextIds) {
        setRegistros(dados);
      }
    };

    // Poll inicial imediato
    poll();

    // Poll periódico enquanto Firestore offline
    pollIntervalRef.current = setInterval(poll, LOCAL_POLL_INTERVAL);
  }, [lerLocalStorage, deduplicar, setRegistros]);

  // ── Parar polling ─────────────────────────────────────────────────────────
  const pararPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  // ── Setup principal — V17 DATA CONTEXT + CACHE-FIRST ──────────────────────
  useEffect(() => {
    mountedRef.current = true;
    firestoreAtivoRef.current = false;

    // ======================================================
    // 🔥 V16 PASSO 1: CACHE-FIRST — DataContext/IndexedDB IMEDIATO
    //    A UI mostra dados em <16ms. O usuário NUNCA vê
    //    tela vazia enquanto Firestore carrega.
    // ======================================================
    const initData = async () => {
      try {
        // Intentar DataContext primero (async)
        const dadosContext = await lerDataContext();
        
        if (dadosContext && dadosContext.length > 0) {
          const dedup = deduplicar(dadosContext);
          lastKnownDataRef.current = dedup;
          if (mountedRef.current) setRegistrosRaw(dedup);
          console.log(`📦 [V17] Cache-First DataContext: ${dedup.length} registros`);
        } else {
          // Fallback a localStorage
          const dadosLocais = deduplicar(lerLocalStorage());
          if (dadosLocais.length > 0) {
            lastKnownDataRef.current = dadosLocais;
            if (mountedRef.current) setRegistrosRaw(dadosLocais);
            console.log(`📦 Cache-First localStorage: ${dadosLocais.length} registros`);
          } else {
            lastKnownDataRef.current = [];
            if (mountedRef.current) setRegistrosRaw([]);
          }
        }
      } catch (error) {
        console.error('❌ [V17] Error en carga inicial:', error);
        // Último fallback: localStorage directo
        const dadosLocais = deduplicar(lerLocalStorage());
        lastKnownDataRef.current = dadosLocais;
        if (mountedRef.current) setRegistrosRaw(dadosLocais);
      }

      // ======================================================
      // 🔥 PASSO 2: Si NO hay database, usar solo local + polling
      // ======================================================
      if (!database) {
        setLoading(false);
        iniciarPollingLocal();
        return;
      }

      // ======================================================
      // 🔥 PASSO 3: BACKGROUND SYNC — Firestore reconcilia
      // ======================================================
      let firestoreRespondeu = false;

      try {
        const unsubscribe = database.onFinanceiroSnapshot?.(
          (docs) => {
            if (!mountedRef.current) return;

            const docsArray = Array.isArray(docs) ? docs : [];
            
            if (docsArray.length > 0) {
              // ✅ Firestore tem dados → usar como fuente principal
              firestoreRespondeu = true;
              firestoreAtivoRef.current = true;
              pararPolling();
              
              // 🔥 V16: Merge con DataContext/localStorage
              const firestoreIds = new Map(docsArray.map(d => [d.id, d]));
              const dadosLocaisAtuais = lerLocalStorage();
              
              // Adicionar itens locais que não estão no Firestore (offline pending)
              for (const item of dadosLocaisAtuais) {
                if (!firestoreIds.has(item.id) && !firestoreIds.has(item._localId)) {
                  docsArray.push(item);
                }
              }
              
              const dedup = deduplicar(docsArray);
              lastKnownDataRef.current = dedup;
              if (mountedRef.current) setRegistrosRaw(dedup);
              setFirestoreOnline(true);
              console.log(`☁️ Firestore: ${dedup.length} registros (merge con local)`);
            } else {
              // ⚠️ Firestore retornou vazio
              if (!firestoreRespondeu) {
                const locais = deduplicar(lerLocalStorage());
                if (locais.length > 0) {
                  lastKnownDataRef.current = locais;
                  if (mountedRef.current) setRegistrosRaw(locais);
                  console.log(`📦 Firestore vazio, mantendo ${locais.length} registros locais`);
                  iniciarPollingLocal();
                } else {
                  lastKnownDataRef.current = [];
                  if (mountedRef.current) setRegistrosRaw([]);
                }
              }
              setFirestoreOnline(false);
            }

            if (mountedRef.current) setLoading(false);
          },
          (error) => {
            if (!mountedRef.current) return;
            console.warn("⚠️ Firestore offline:", error?.message);
            setFirestoreOnline(false);
            
            // 🔥 V16: Fallback — NUNCA substitui por vazio
            const locais = deduplicar(lerLocalStorage());
            if (locais.length > 0 || lastKnownDataRef.current.length === 0) {
              lastKnownDataRef.current = locais;
              if (mountedRef.current) setRegistrosRaw(locais);
            }
            iniciarPollingLocal();
            if (mountedRef.current) setLoading(false);
          }
        );

        unsubscribeRef.current = typeof unsubscribe === "function" ? unsubscribe : null;
      } catch (e) {
        console.warn('⚠️ [V17] onFinanceiroSnapshot não disponível, usando apenas local');
        setFirestoreOnline(false);
        if (mountedRef.current) setLoading(false);
        iniciarPollingLocal();
      }
    };

    initData();

    // ── Storage event listener (cross-tab sync) ────────────────────────────
    const handleStorageEvent = (e) => {
      if (!mountedRef.current) return;
      if (
        e.key === FINANCE_STORAGE_EVENT_KEY ||
        e.key?.includes("imoveispro_db_user_")
      ) {
        const dados = deduplicar(lerLocalStorage());
        // Solo actualizar si hay cambios reales
        const prevIds = lastKnownDataRef.current.map(r => r.id).join(",");
        const nextIds = dados.map(r => r.id).join(",");
        if (prevIds !== nextIds) {
          lastKnownDataRef.current = dados;
          setRegistrosRaw(dados);
        }
      }
    };

    window.addEventListener("storage", handleStorageEvent);

    // ── Timeout de segurança: se Firestore não responder em 4s → assume offline ──
    const safetyTimeout = setTimeout(() => {
      if (!mountedRef.current || firestoreAtivoRef.current) return;
      if (loading) {
        const local = deduplicar(lerLocalStorage());
        lastKnownDataRef.current = local;
        if (mountedRef.current) setRegistrosRaw(local);
        if (mountedRef.current) setLoading(false);
        iniciarPollingLocal();
        console.log(`⏰ Timeout Firestore, usando ${local.length} registros locais`);
      }
    }, 4000);

    // ── 🔥 V16: Observer do DataContext para atualizações em tempo real ────
    let observerCleanup = null;
    
    getDataContext().then(ctx => {
      if (!mountedRef.current || !ctx) return;
      
      if (typeof ctx.addObserver === 'function') {
        const observer = (event) => {
          if (!mountedRef.current) return;
          
          // Solo reaccionar a cambios en financeiro
          if (event?.collection === 'financeiro' || event?.type === 'DATACHANGE') {
            // Re-leer datos para mantener consistencia
            ctx.collections.financeiro.read().then(dados => {
              if (!mountedRef.current) return;
              if (Array.isArray(dados)) {
                const prevIds = lastKnownDataRef.current.map(r => r.id).join(",");
                const nextIds = dados.map(r => r.id).join(",");
                if (prevIds !== nextIds) {
                  console.log(`🔄 [V17] DataContext sync: ${dados.length} registros`);
                  const dedup = deduplicar(dados);
                  lastKnownDataRef.current = dedup;
                  setRegistrosRaw(dedup);
                }
              }
            }).catch(() => {});
          }
        };
        
        ctx.addObserver(observer);
        observerIdRef.current = observer;
      }
    }).catch(() => {});

    // ── Cleanup ──────────────────────────────────────────────────────────
    return () => {
      mountedRef.current = false;
      clearTimeout(safetyTimeout);
      window.removeEventListener("storage", handleStorageEvent);

      if (unsubscribeRef.current) {
        try { unsubscribeRef.current(); } catch (_) {}
        unsubscribeRef.current = null;
      }

      // Remover observer do DataContext
      if (observerIdRef.current && dataContextRef.current) {
        try {
          // DataContext no tiene removeObserver explícito, pero podemos
          // limpiar la referencia para que el GC lo recolecte
          observerIdRef.current = null;
        } catch (_) {}
      }

      pararPolling();
    };
  }, [database, lerLocalStorage, deduplicar, iniciarPollingLocal, pararPolling, lerDataContext, getDataContext]);

  // ── 🔥 V16: handleRefresh manual (usa DataContext primero) ────────────────
  const handleRefresh = useCallback(async () => {
    setLoading(true);
    
    try {
      // Intentar DataContext primero
      const dadosContext = await lerDataContext();
      if (dadosContext && dadosContext.length > 0) {
        const dedup = deduplicar(dadosContext);
        lastKnownDataRef.current = dedup;
        setRegistrosRaw(dedup);
      } else {
        // Fallback a localStorage
        const dados = deduplicar(lerLocalStorage());
        lastKnownDataRef.current = dados;
        setRegistrosRaw(dados);
      }
    } catch (error) {
      console.error('❌ [V17] Error en handleRefresh:', error);
      const dados = deduplicar(lerLocalStorage());
      lastKnownDataRef.current = dados;
      setRegistrosRaw(dados);
    }
    
    setLoading(false);
  }, [lerLocalStorage, deduplicar, lerDataContext]);

  // ── 🔥 V17: notifyLocalChange (dispara storage event + 'dataUpdated' + refresco) ──
  const notifyLocalChange = useCallback(() => {
    try {
      localStorage.setItem(
        FINANCE_STORAGE_EVENT_KEY,
        String(Date.now())
      );
    } catch (_) {}
    
    // Actualizar RAM inmediatamente desde localStorage
    const dados = deduplicar(lerLocalStorage());
    lastKnownDataRef.current = dados;
    setRegistrosRaw(dados);

    // 🆕 V17: Notificar a otros componentes (Reports, Dashboard, etc.)
    // Reports.jsx escucha este evento para actualizarse en tiempo real
    window.dispatchEvent(new CustomEvent('dataUpdated'));
  }, [lerLocalStorage, deduplicar]);

  // ── Return ────────────────────────────────────────────────────────────────
  return {
    registros,
    setRegistros,
    loading,
    mes,
    setMes,
    handleRefresh,
    notifyLocalChange,
    firestoreOnline,
    getDataContext,  // 🆕 Expuesto para uso externo
  };
};

export default useFinanceData;